<!-- eslint-disable vue/no-ref-as-operand -->
<script lang="ts">
import {
  computed, defineComponent, ref, shallowRef, toRef, watch, PropType, Ref,
} from 'vue';
import {
  usePendingSaveCount, useHandler, useTrackFilters, useRevisionId,
} from 'vue-media-annotator/provides';
import { buildPerCameraRegistrationFiles } from 'vue-media-annotator/alignedView/cameraRegistrationFiles';
import { referenceCameraName } from 'dive-common/multicamDisplay';
import AutosavePrompt from 'dive-common/components/AutosavePrompt.vue';
import { useRequest } from 'dive-common/use';
import {
  DatasetSourceMedia, getDataset, getDatasetMedia, getUri,
  hasCalibrationFile, downloadCalibration,
} from 'platform/web-girder/api';
import { GirderConfigStatic } from 'platform/web-girder/constants';
import {
  ImageSequenceType, LargeImageType, MultiType, VideoType,
} from 'dive-common/constants';

export default defineComponent({
  components: { AutosavePrompt },

  props: {
    datasetIds: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    fileIds: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    blockOnUnsaved: {
      type: Boolean,
      default: false,
    },
    buttonOptions: {
      type: Object,
      default: () => ({}),
    },
    menuOptions: {
      type: Object,
      default: () => ({}),
    },
  },

  setup(props) {
    const savePrompt = ref(false);
    let currentSaveUrl = '';

    /** State populated from provides if the dialog exists inside a viewer context */
    let save = () => Promise.resolve();
    let pendingSaveCount = ref(0);
    let checkedTypes = ref([] as readonly string[]);
    let revisionId = ref(null as null | number);
    if (props.blockOnUnsaved) {
      save = useHandler().save;
      pendingSaveCount = usePendingSaveCount();
      checkedTypes = useTrackFilters().checkedTypes;
      revisionId = useRevisionId();
    }

    async function doExport({ forceSave = false, url }: { url?: string; forceSave?: boolean }) {
      if (pendingSaveCount.value > 0 && forceSave) {
        try {
          await save();
        } finally {
          savePrompt.value = false;
        }
      } else if (pendingSaveCount.value > 0 && url) {
        savePrompt.value = true;
        currentSaveUrl = url;
        return;
      }
      if (url) {
        window.location.assign(url);
      } else if (forceSave && currentSaveUrl) {
        window.location.assign(currentSaveUrl);
      } else {
        throw new Error('Expected either url OR forceSave and currentSaveUrl');
      }
    }

    const isDownloadButtonDisplayed = computed(() => {
      const datasetsSelected = props.datasetIds.length > 0;
      const filesSelected = props.fileIds.length > 0;

      return datasetsSelected !== filesSelected;
    });

    const isDatasetDownload = computed(() => props.datasetIds.length > 0 && props.fileIds.length === 0);
    const isFilesDownload = computed(() => props.fileIds.length > 0 && props.datasetIds.length === 0);

    const menuOpen = ref(false);
    const excludeBelowThreshold = ref(true);
    const excludeUncheckedTypes = ref(false);
    const cameraFileSupported = ref(false);

    const singleDataSetId: Ref<string|null> = ref(null);
    const dataset = shallowRef(null as GirderConfigStatic | null);
    const datasetMedia = shallowRef(null as DatasetSourceMedia | null);
    const { request, error } = useRequest();
    const loadDatasetConfig = () => request(async () => {
      if (props.datasetIds.length > 1) {
        singleDataSetId.value = null;
        dataset.value = null;
      } else {
        [singleDataSetId.value] = props.datasetIds;
      }
      if (menuOpen.value && singleDataSetId.value) {
        dataset.value = (await getDataset(singleDataSetId.value)).data;
        if (dataset.value.type === 'video') {
          datasetMedia.value = (await getDatasetMedia(singleDataSetId.value)).data;
        }
        cameraFileSupported.value = dataset.value.subType === 'stereo'
          && await hasCalibrationFile(singleDataSetId.value);
      } else {
        cameraFileSupported.value = false;
      }
    });
    watch([toRef(props, 'datasetIds'), menuOpen], loadDatasetConfig);

    const exportUrls = computed(() => {
      const params = {
        excludeBelowThreshold: excludeBelowThreshold.value,
        typeFilter: excludeUncheckedTypes.value ? JSON.stringify(checkedTypes.value) : undefined,
      };
      if (props.fileIds.length > 0) {
        if (props.fileIds.length === 1) {
          return {
            exportAllUrl: getUri({
              url: `item/${props.fileIds[0]}/download`,
            }),
          };
        }
        return {
          exportAllUrl: getUri({
            url: 'resource/download',
            params: {
              resources: JSON.stringify({ item: props.fileIds }),
            },
          }),
        };
      }
      if (singleDataSetId.value) {
        const isMulticam = dataset.value?.type === MultiType;
        let exportMediaUrl;
        if (isMulticam) {
          exportMediaUrl = undefined;
        } else if (dataset.value?.type === 'video') {
          exportMediaUrl = datasetMedia.value?.video?.url;
        } else {
          exportMediaUrl = getUri({
            url: 'dive_dataset/export',
            params: {
              ...params,
              includeDetections: false,
              includeMedia: true,
              folderIds: JSON.stringify([singleDataSetId.value]),
            },
          });
        }
        return {
          exportAllUrl: getUri({
            url: 'dive_dataset/export',
            params: {
              ...params,
              folderIds: JSON.stringify([singleDataSetId.value]),
              includeMedia: true,
              includeDetections: true,
            },
          }),
          exportMediaUrl,
          exportDetectionsUrl: getUri({
            url: 'dive_annotation/export',
            params: {
              ...params,
              folderId: singleDataSetId.value,
              revisionId: revisionId.value,
            },
          }),
          exportDetectionsUrlTrackJSON: getUri({
            url: 'dive_annotation/export',
            params: {
              ...params,
              folderId: singleDataSetId.value,
              revisionId: revisionId.value,
              format: 'dive_json',
            },
          }),
          exportDetectionsUrlCocoJSON: getUri({
            url: 'dive_annotation/export',
            params: {
              ...params,
              folderId: singleDataSetId.value,
              revisionId: revisionId.value,
              format: 'coco_json',
            },
          }),
          exportConfigurationUrl: getUri({
            url: `dive_dataset/${singleDataSetId.value}/configuration`,
          }),
        };
      }
      return {
        exportAllUrl: getUri({
          url: 'dive_dataset/export',
          params: { folderIds: JSON.stringify(props.datasetIds) },

        }),
        exportAllUrlDetections: getUri({
          url: 'dive_dataset/export',
          params: {
            ...params,
            folderIds: JSON.stringify(props.datasetIds),
            includeDetections: true,
            includeMedia: false,
          },
        }),

      };
    });

    const isMulticamDataset = computed(() => dataset.value?.type === MultiType);

    const mediaType = computed(() => {
      if (dataset.value === null || dataset.value.type === MultiType) return null;
      const { type } = dataset.value;
      return {
        [ImageSequenceType]: 'Image Sequence',
        [VideoType]: 'Video',
        [LargeImageType]: 'Tiled Images',
      }[type];
    });

    async function prepareExport() {
      if (isFilesDownload.value) {
        menuOpen.value = false;
        await doExport({ url: exportUrls.value && exportUrls.value.exportAllUrl });
      } else {
        menuOpen.value = true;
      }
    }

    async function exportCameraFile() {
      if (!singleDataSetId.value) {
        return;
      }
      await request(async () => {
        await downloadCalibration(singleDataSetId.value as string);
        menuOpen.value = false;
      });
    }

    // Per-camera registration files, built client-side from the
    // dataset meta (the calibration persists there on web). Each pair files
    // under its non-reference camera, matching the desktop's on-disk
    // <camera>_to_<reference>_registration.json convention.
    const registrationFiles = computed(() => {
      const ds = dataset.value;
      if (!ds || ds.type !== MultiType) {
        return [];
      }
      return buildPerCameraRegistrationFiles({
        homographies: ds.cameraHomographies ?? {},
        observations: ds.cameraCorrespondences ?? {},
        transformTypes: ds.cameraTransformTypes ?? {},
        source: ds.cameraRegistrationSource ?? null,
      }, referenceCameraName(ds.multiCamMedia));
    });

    function exportRegistration(camera: string) {
      const match = registrationFiles.value.find((file) => file.camera === camera);
      if (!match) {
        return;
      }
      const blob = new Blob(
        [JSON.stringify(match.body, null, 2)],
        { type: 'application/json' },
      );
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = match.name;
      anchor.click();
      URL.revokeObjectURL(url);
    }

    return {
      error,
      dataset,
      mediaType,
      excludeBelowThreshold,
      excludeUncheckedTypes,
      menuOpen,
      prepareExport,
      exportUrls,
      checkedTypes,
      revisionId,
      savePrompt,
      singleDataSetId,
      doExport,
      isDownloadButtonDisplayed,
      isDatasetDownload,
      isFilesDownload,
      isMulticamDataset,
      cameraFileSupported,
      exportCameraFile,
      registrationFiles,
      exportRegistration,
    };
  },
});
</script>

<template>
  <v-menu
    v-model="menuOpen"
    :close-on-content-click="false"
    :open-on-click="false"
    :nudge-width="120"
    v-bind="menuOptions"
    max-width="280"
  >
    <template #activator="{ on: menuOn }">
      <v-tooltip bottom>
        <template #activator="{ on: tooltipOn }">
          <v-btn
            class="ma-0"
            v-bind="buttonOptions"
            :disabled="!isDownloadButtonDisplayed"
            v-on="{ ...tooltipOn, ...menuOn }"

            @click="prepareExport()"
          >
            <v-icon>
              mdi-download
            </v-icon>
            <span
              v-show="!$vuetify.breakpoint.mdAndDown || buttonOptions.block"
              class="pl-1"
            >
              Download
            </span>
            <v-spacer />
            <v-icon v-if="menuOptions.right && isDatasetDownload">
              mdi-chevron-right
            </v-icon>
          </v-btn>
        </template>
        <span v-if="isDatasetDownload">Download media and annotations</span>
        <span v-else-if="isFilesDownload">Download selected files</span>
      </v-tooltip>
    </template>
    <template>
      <AutosavePrompt
        v-model="savePrompt"
        @save="doExport({ forceSave: true })"
      />
      <v-card
        outlined
        class="downloadMenu"
      >
        <v-card-title>
          Download options
        </v-card-title>
        <v-alert
          v-if="revisionId"
          type="info"
          tile
        >
          Revision {{ revisionId }} selected
        </v-alert>
        <v-alert
          v-if="error"
          color="error"
          class="mx-2"
        >
          <p class="text-h5">
            Error
          </p>
          {{ error }}
        </v-alert>

        <template v-if="dataset !== null && (mediaType !== null || isMulticamDataset)">
          <template v-if="mediaType !== null">
            <v-card-text class="pb-0">
              Zip all {{ mediaType }} files only
            </v-card-text>
            <v-card-actions>
              <v-btn
                depressed
                block
                target="_blank"
                rel="noopener"
                :disabled="!exportUrls.exportMediaUrl"
                :href="exportUrls.exportMediaUrl"
              >
                {{ mediaType }}
              </v-btn>
            </v-card-actions>
          </template>

          <v-card-text class="pb-2">
            <div v-if="isMulticamDataset">
              Get latest annotations for all cameras (zip)
            </div>
            <div v-else>
              Get latest annotation csv only
            </div>
            <template v-if="dataset.confidenceFilters || true">
              <v-checkbox
                v-model="excludeBelowThreshold"
                label="exclude tracks below confidence threshold"
                dense
                hide-details
              />
              <div class="pt-2">
                <span>Current thresholds:</span>
                <span
                  v-for="(val, key) in dataset.confidenceFilters"
                  :key="key"
                  class="pt-2"
                >
                  ({{ key }}, {{ val }})
                </span>
              </div>
            </template>

            <template v-if="checkedTypes.length">
              <v-checkbox
                v-model="excludeUncheckedTypes"
                label="export checked types only"
                dense
                hint="Export only stored confidence pairs whose raw type names are checked; other pairs are removed from exported tracks"
                persistent-hint
                class="pt-0"
              />
            </template>
          </v-card-text>

          <v-card-actions>
            <v-row>
              <v-col>
                <v-btn
                  depressed
                  block
                  :disabled="!exportUrls.exportDetectionsUrl"
                  @click="doExport({ url: exportUrls && exportUrls.exportDetectionsUrl })"
                >
                  <span
                    v-if="exportUrls.exportDetectionsUrl"
                  >VIAME CSV</span>
                  <span
                    v-else
                  >detections unavailable</span>
                </v-btn>
                <v-btn
                  depressed
                  block
                  class="mt-2"
                  :disabled="!exportUrls.exportDetectionsUrl"
                  @click="doExport({
                    url: exportUrls
                      && exportUrls.exportDetectionsUrlCocoJSON,
                  })"
                >
                  <span
                    v-if="exportUrls.exportDetectionsUrl"
                  >COCO JSON</span>
                  <span
                    v-else
                  >detections unavailable</span>
                </v-btn>
                <v-btn
                  depressed
                  block
                  class="mt-2"
                  :disabled="!exportUrls.exportDetectionsUrl"
                  @click="doExport({
                    url: exportUrls
                      && exportUrls.exportDetectionsUrlTrackJSON,
                  })"
                >
                  <span
                    v-if="exportUrls.exportDetectionsUrl"
                  >DIVE JSON</span>
                  <span
                    v-else
                  >detections unavailable</span>
                </v-btn>
                <!-- <v-btn
              depressed
              block
              :disabled="!exportUrls.exportDetectionsUrl"
              @click="doExport({ url: exportUrls && exportUrls.exportDetectionsUrl })"
            >
              <span v-if="exportUrls.exportDetectionsUrl">annotations</span>
              <span v-else>detections unavailable</span>
            </v-btn> -->
              </v-col>
            </v-row>
          </v-card-actions>

          <v-card-text class="pb-0">
            Export the dataset configuration, including
            attribute definitions, types, styles, thresholds, and dataset info.
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              depressed
              block
              @click="doExport({ url: exportUrls && exportUrls.exportConfigurationUrl })"
            >
              Configuration
            </v-btn>
          </v-card-actions>

          <template v-if="cameraFileSupported">
            <v-card-text class="pb-0">
              Download the stereo camera / calibration file currently associated
              with this dataset.
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                depressed
                block
                @click="exportCameraFile"
              >
                Camera File
              </v-btn>
            </v-card-actions>
          </template>

          <template v-if="registrationFiles.length">
            <v-card-text class="pb-0">
              Download the camera registration: one registration file per camera.
            </v-card-text>
            <v-card-actions>
              <v-row>
                <v-col>
                  <v-btn
                    v-for="file in registrationFiles"
                    :key="file.camera"
                    depressed
                    block
                    class="my-1"
                    :title="`Registration: ${file.camera}${file.destination ? ` → ${file.destination}` : ''}`"
                    @click="exportRegistration(file.camera)"
                  >
                    <span class="registration-export-btn__label">
                      Registration: {{ file.camera }}{{ file.destination ? ` → ${file.destination}` : '' }}
                    </span>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-actions>
          </template>

          <v-card-text class="pb-0">
            <span v-if="isMulticamDataset">
              Zip all cameras: media, annotations, calibration, and dataset metadata
            </span>
            <span v-else>
              Zip all media, detections, and edit history recursively from all sub-folders
            </span>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              depressed
              block
              @click="doExport({ url: exportUrls && exportUrls.exportAllUrl })"
            >
              Everything
            </v-btn>
          </v-card-actions>
        </template>
        <template v-else-if="exportUrls.exportAllUrl !== undefined">
          <v-card-text class="pb-0">
            Zip all media, detections, and edit history from all selected dataset folders
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              depressed
              block
              @click="doExport({ url: exportUrls && exportUrls.exportAllUrl })"
            >
              Everything
            </v-btn>
          </v-card-actions>
          <v-card-text class="pb-0">
            Export All selected Dataset Detections in VIAME CSV and DIVE JSON
          </v-card-text>
          <v-checkbox
            v-model="excludeBelowThreshold"
            label="exclude tracks below confidence threshold"
            dense
            hide-details
          />

          <v-card-actions>
            <v-spacer />
            <v-btn
              depressed
              block
              @click="doExport({ url: exportUrls && exportUrls.exportAllUrlDetections })"
            >
              Detections
            </v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </template>
  </v-menu>
</template>

<style scoped>
.downloadMenu {
  max-height: calc(100vh - 30px);
  overflow-y: auto;
  overflow-x: hidden;
}

/* Long camera names overflow the block button otherwise; the full label is
   still available via the button's native title tooltip on hover. */
.registration-export-btn__label {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
